import React, { useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import i18next from '../../i18n.js';
import { acceptAdditionalInputFileString, allowedAdditionalFileExtensions, acceptImageFileString, allowedImageFileExtensions, maxFileSize, attachmentTypes } from '../../constants.js';
import { getFileAttachmentErrorMessage } from '../helpers/CheckFunctions.js';
import { isMobile, getDodumentFiles, copyTextToClipboard } from '../helpers/HelperFunctions.js';
import { errorMessageDialog, addLinkDialog, viewFile, viewImage, unknownErrorMessageDialog } from '../ModalDialog.js';

const DocIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B7CB0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 3h8l5 5v13H6V3z" />
        <path d="M14 3v5h5" />
    </svg>
);
const LinkIcon = (props) => (
    <svg width={props.size ?? 22} height={props.size ?? 22} viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 15l6-6" />
        <path d="M10.5 6.5l1.5-1.5a3.5 3.5 0 015 5l-2 2" />
        <path d="M13.5 17.5L12 19a3.5 3.5 0 01-5-5l2-2" />
    </svg>
);
const UploadIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#5C5C5C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 15V4M12 4L8 8M12 4l4 4" />
        <path d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" />
    </svg>
);
const CameraIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 8.5A2 2 0 015 6.5h2.2l1.2-2h7.2l1.2 2H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9z" />
        <circle cx="12" cy="13" r="3.6" />
    </svg>
);
const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);
const DownloadIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 4v11M12 15l-4-4M12 15l4-4" />
        <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
    </svg>
);
const ViewIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);
const CopyIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="11" height="11" rx="2.5" />
        <path d="M15 6.5V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7a2 2 0 002 2h.5" />
    </svg>
);

// Переглянути можна лише те, що вміє показати діалог: PDF або зображення
const canViewItem = (item) => item.type !== attachmentTypes.link &&
    (item.value?.mimeType === 'application/pdf' || acceptImageFileString.split(',').includes(item.value?.mimeType));


// Поле для додавання документів до відрядження. Значення — список елементів { type, value },
// де для файлів у value лежить сам файл, а для посилань — адреса.
const AttachmentsInput = (props) => {
    const { name, value, helpText, allowLinks, allowPhoto, noFilesLabel, required, submitted, disabled } = props;
    const fileInputRef = useRef(null);
    const photoInputRef = useRef(null);
    const [copiedIndex, setCopiedIndex] = useState(null);

    const items = value ?? [];
    // фото робиться камерою пристрою, тому на десктопі кнопку не показуємо
    const showPhotoBtn = allowPhoto && isMobile();

    const changeItems = (updatedItems) => {
        if (props.onChange) {
            props.onChange({
                target: {
                    name: name,
                    value: updatedItems
                }
            });
        }
    }

    const addItem = (item) => changeItems([...items, item]);

    const removeItem = (index) => {
        const updatedItems = [...items];
        updatedItems.splice(index, 1);
        changeItems(updatedItems);
    }

    const fileAddedHandle = (e, allowedExtensions) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }
        const errorMessage = getFileAttachmentErrorMessage(file, allowedExtensions, maxFileSize);
        e.target.value = '';
        if (errorMessage) {
            errorMessageDialog(errorMessage);
            return;
        }
        const fileReader = new FileReader();
        fileReader.addEventListener('load', () => {
            addItem({
                type: attachmentTypes.file,
                value: {
                    fileName: file.name,
                    mimeType: file.type,
                    size: file.size,
                    content: fileReader.result
                }
            });
        }, false);
        fileReader.readAsDataURL(file);
    }

    const addLinkHandle = () => addLinkDialog((link) => addItem({ type: attachmentTypes.link, value: link }));

    // Щойно доданий файл уже містить вміст, а збережений раніше має лише nodeRef —
    // такий довантажуємо з сервера тим самим запитом, що й AttachmentsForm
    const getItemFile = async (item) => {
        if (item.value?.content) {
            return item.value;
        }
        if (!item.nodeRef) {
            return null;
        }
        const result = await getDodumentFiles('/api/file/GetAttachedFiles', [{ nodeRef: item.nodeRef }]);
        return result?.[0]?.file ?? null;
    }

    const downloadItemHandle = async (item) => {
        const file = await getItemFile(item);
        if (!file) {
            unknownErrorMessageDialog();
            return;
        }
        const anchor = document.createElement('a');
        anchor.href = file.content;
        anchor.download = file.fileName;
        anchor.click();
    }

    const viewItemHandle = async (item) => {
        const file = await getItemFile(item);
        if (!file) {
            unknownErrorMessageDialog();
            return;
        }
        if (file.mimeType === 'application/pdf') {
            viewFile(file.content, file.fileName, false);
        }
        else {
            viewImage(file.content);
        }
    }

    // підказка «скопійовано» показується біля своєї кнопки і сама зникає
    const copyLinkHandle = (item, index) => {
        copyTextToClipboard(item.value);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(current => (current === index ? null : current)), 2000);
    }

    const getItemName = (item) => item.type === attachmentTypes.link ? i18next.t('document.attachmentLink') : item.value?.fileName;
    // у файлів другого рядка немає — лишається тільки назва, у посилань під назвою сама адреса
    const getItemDescription = (item) => item.type === attachmentTypes.link ? item.value : '';

    return (<div className={'attachments-input' + (disabled ? ' disabled' : '')} id={name} name={name} >
        {/* без можливості додавати нічого не показується, тому пояснюємо, що вкладень немає */}
        {disabled && items.length === 0 && <div className="no-added-label">{noFilesLabel ?? i18next.t('document.attachmentsNotAddedLabel')}</div>}
        {items.length > 0 && <div className="attachments-items">
            {items.map((item, index) => <div className="attachments-item" key={index}>
                {item.type === attachmentTypes.link ? <LinkIcon /> : <DocIcon />}
                <div className="attachments-item-info">
                    <div className="attachments-item-name" title={getItemName(item)}>{getItemName(item)}</div>
                    {getItemDescription(item) && <div className="attachments-item-description">{getItemDescription(item)}</div>}
                </div>
                {item.type === attachmentTypes.link
                    ? <OverlayTrigger placement="top" trigger={[]} show={copiedIndex === index}
                        overlay={<Tooltip id={`${name}-link-copied-${index}`}>{i18next.t('document.attachmentLinkCopied')}</Tooltip>}>
                        <button type="button" className="attachments-item-action" title={i18next.t('document.attachmentCopyLink')}
                            onClick={() => copyLinkHandle(item, index)}>
                            <CopyIcon />
                        </button>
                    </OverlayTrigger>
                    : <>
                        {canViewItem(item) && <button type="button" className="attachments-item-action" title={i18next.t('view')}
                            onClick={() => viewItemHandle(item)}>
                            <ViewIcon />
                        </button>}
                        <button type="button" className="attachments-item-action" title={i18next.t('download')}
                            onClick={() => downloadItemHandle(item)}>
                            <DownloadIcon />
                        </button>
                    </>}
                {!disabled && <button type="button" className="attachments-item-remove" onClick={() => removeItem(index)}>
                    <CloseIcon />
                </button>}
            </div>)}
        </div>}
        {!disabled && <div className="attachments-dropzone">
            <div className={'attachments-buttons' + (showPhotoBtn ? ' with-photo' : '')}>
                <button type="button" className="attachments-btn upload" onClick={() => fileInputRef.current.click()}>
                    <UploadIcon />{i18next.t('document.attachmentUploadBtn')}
                </button>
                {showPhotoBtn && <>
                    <span className="attachments-or">{i18next.t('document.attachmentOr')}</span>
                    <button type="button" className="attachments-btn photo" onClick={() => photoInputRef.current.click()}>
                        <CameraIcon />{i18next.t('document.attachmentPhotoBtn')}
                    </button>
                </>}
                {allowLinks && <button type="button" className="attachments-btn link" onClick={addLinkHandle}>
                    <LinkIcon size={16} />{i18next.t('document.attachmentLink')}
                </button>}
            </div>
            {helpText && <p className={'attachments-hint' + (allowPhoto ? ' faint' : '')} dangerouslySetInnerHTML={{ __html: helpText }} />}
        </div>}
        <input ref={fileInputRef} accept={acceptAdditionalInputFileString} onChange={(e) => fileAddedHandle(e, allowedAdditionalFileExtensions)} type="file" style={{ display: 'none' }} />
        {showPhotoBtn && <input ref={photoInputRef} accept={acceptImageFileString} capture="environment"
            onChange={(e) => fileAddedHandle(e, allowedImageFileExtensions)} type="file" style={{ display: 'none' }} />}
        {required && submitted && items.length === 0 && <div className="help-block">{i18next.t('requiredField')}</div>}
    </div>);
}

export default AttachmentsInput;
